import { useRef, FormEvent } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TextFieldElement } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import { TipShareFormData, DistributionType } from '../types';
import {
  percentageInputBoxSx,
  sectionSpacingSx,
  sectionTitleSx,
  calculationRowSx,
  tipShareAmountTitleSx,
  netSalesRowSx,
  formControlLabelMarginBottomSx,
  rowFlexWithCenterAlignmentMediumGapSx,
  rowFlexWithCenterAlignmentSmallMarginSx,
  tooltipWhiteSpacePreLineSx,
  rowFlexWithLargeGapSx,
  calculationBreakdownMaxWidthSx,
} from '../styles';

// Example calculation constants
const GROSS_SALES = 500.0;
const COMPS_AMOUNT = 25.0;
const PROMOTIONS_AMOUNT = 50.0;
const TAX_AMOUNT = 45.0;

interface TipShareContributionFormProps {
  isReadOnly?: boolean;
}

export default function TipShareContributionForm({ isReadOnly = false }: TipShareContributionFormProps) {
  const { t } = useTranslation();
  const { setValue, control } = useFormContext<TipShareFormData>();
  const prevValueRef = useRef<string>('');

  // Watch checkbox and percentage values using useWatch
  const enableTipShareContribution = useWatch({ control, name: 'enableTipShareContribution' });
  const contributionPercentage = useWatch({ control, name: 'contributionPercentage' });
  const includeComps = useWatch({ control, name: 'includeComps' });
  const includePromotions = useWatch({ control, name: 'includePromotions' });
  const includeTax = useWatch({ control, name: 'includeTax' });
  const distributionType = useWatch({ control, name: 'distributionType' });

  // Contribution toggle should be disabled and auto-set based on distribution type:
  // - AUTOMATIC → ON and disabled (must remain ON)
  // - MANUAL → OFF and disabled (must remain OFF)
  // - No type selected (undefined) → User can toggle freely
  const isContributionToggleDisabled =
    distributionType === DistributionType.AUTOMATIC || distributionType === DistributionType.MANUAL;

  // Calculate net sales based on selected checkboxes
  let netSales = GROSS_SALES;
  if (includeComps) netSales -= COMPS_AMOUNT;
  if (includePromotions) netSales -= PROMOTIONS_AMOUNT;
  if (includeTax) netSales -= TAX_AMOUNT;

  // Calculate tip-share amount
  const tipShareAmount = (netSales * (contributionPercentage || 0)) / 100;

  // Format currency
  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;

  // Calculation row configurations
  const deductionRows = [
    {
      key: 'comps',
      label: t('tipShare.contribution.lessComps'),
      amount: COMPS_AMOUNT,
      isActive: includeComps,
    },
    {
      key: 'promotions',
      label: t('tipShare.contribution.lessPromotions'),
      amount: PROMOTIONS_AMOUNT,
      isActive: includePromotions,
    },
    {
      key: 'tax',
      label: t('tipShare.contribution.lessTax'),
      amount: TAX_AMOUNT,
      isActive: includeTax,
    },
  ];

  // Checkbox configurations
  const checkboxConfigs = [
    {
      key: 'comps',
      fieldName: 'includeComps' as const,
      checked: includeComps,
      testId: 'comps-checkbox',
      label: t('tipShare.contribution.compsLabel'),
    },
    {
      key: 'promotions',
      fieldName: 'includePromotions' as const,
      checked: includePromotions,
      testId: 'promotions-checkbox',
      label: t('tipShare.contribution.promotionsLabel'),
    },
    {
      key: 'tax',
      fieldName: 'includeTax' as const,
      checked: includeTax,
      testId: 'tax-checkbox',
      label: t('tipShare.contribution.taxLabel'),
    },
  ];

  return (
    <Box data-testid='tip-share-contribution-form'>
      {/* Enable tip-share contribution toggle */}
      <Tooltip
        title={
          enableTipShareContribution && isContributionToggleDisabled
            ? t('tipShare.contribution.disabledToggleTooltip')
            : ''
        }
        arrow
        placement='right'
      >
        <FormControlLabel
          control={
            <Switch
              checked={enableTipShareContribution}
              onChange={(e) => setValue('enableTipShareContribution', e.target.checked, { shouldDirty: true })}
              disabled={isContributionToggleDisabled || isReadOnly}
              data-testid='enable-tip-share-contribution-switch'
            />
          }
          label={t('tipShare.contribution.enableTipShareContribution')}
          sx={formControlLabelMarginBottomSx}
        />
      </Tooltip>

      {/* Show form only when toggle is enabled */}
      {enableTipShareContribution && (
        <>
          {/* Tip share percentage input */}
          <Box sx={rowFlexWithCenterAlignmentMediumGapSx}>
            <Box sx={percentageInputBoxSx} data-testid='tip-share-percentage-field-input'>
              <TextFieldElement
                name='contributionPercentage'
                label={t('tipShare.contribution.percentageLabel')}
                type='number'
                variant='outlined'
                fullWidth
                required
                disabled={isReadOnly}
                InputProps={{
                  inputProps: {
                    min: 0,
                    max: 100,
                    step: 0.01,
                    onInput: (e: FormEvent<HTMLInputElement>) => {
                      const input = e.currentTarget;
                      const val = input.value;
                      const dotIndex = val.indexOf('.');
                      // Revert if the resulting value has more than 2 decimal places
                      if (dotIndex !== -1 && val.length - dotIndex - 1 > 2) {
                        input.value = prevValueRef.current;
                        setValue('contributionPercentage', parseFloat(prevValueRef.current) || 0, {
                          shouldDirty: true,
                        });
                      } else {
                        prevValueRef.current = val;
                      }
                    },
                  },
                  startAdornment: <InputAdornment position='start'>%</InputAdornment>,
                }}
              />
            </Box>
            <Typography variant='body2' color='text.secondary'>
              {t('tipShare.contribution.percentageHelper')}
            </Typography>
          </Box>

          {/* Tip-share contribution calculation section */}
          <Box sx={sectionSpacingSx}>
            <Typography variant='body1' sx={sectionTitleSx} fontWeight={600}>
              {t('tipShare.contribution.calculationTitle')}
            </Typography>

            {/* Include in calculation label with info icon */}
            <Box sx={rowFlexWithCenterAlignmentSmallMarginSx}>
              <Typography variant='body1' fontWeight={500}>
                {t('tipShare.contribution.includeInCalculation')}
              </Typography>
              <Tooltip
                title={
                  <Box sx={tooltipWhiteSpacePreLineSx}>{t('tipShare.contribution.includeInCalculationTooltip')}</Box>
                }
                arrow
                placement='right'
                componentsProps={{ tooltip: { sx: { maxWidth: 380 } } }}
              >
                <InfoIcon fontSize='small' color='action' />
              </Tooltip>
            </Box>

            {/* Checkboxes for Comps, Promotions, Tax */}
            <Box sx={rowFlexWithLargeGapSx}>
              {checkboxConfigs.map((config) => (
                <FormControlLabel
                  key={config.key}
                  control={
                    <Checkbox
                      checked={config.checked}
                      onChange={(e) => setValue(config.fieldName, e.target.checked, { shouldDirty: true })}
                      disabled={isReadOnly}
                      data-testid={config.testId}
                    />
                  }
                  label={config.label}
                />
              ))}
            </Box>

            {/* Example calculation section */}
            <Box sx={sectionSpacingSx}>
              <Typography variant='body2' color='text.secondary' sx={sectionTitleSx}>
                {t('tipShare.contribution.exampleCalculation')}
              </Typography>

              {/* Calculation breakdown */}
              <Box sx={calculationBreakdownMaxWidthSx}>
                {/* Gross sales */}
                <Box sx={calculationRowSx}>
                  <Typography variant='body2'>{t('tipShare.contribution.grossSales')}</Typography>
                  <Typography variant='body2' fontWeight={500}>
                    {formatCurrency(GROSS_SALES)}
                  </Typography>
                </Box>

                {/* Deduction rows (Comps, Promotions, Tax) */}
                {deductionRows.map((row) => (
                  <Box key={row.key} sx={calculationRowSx}>
                    <Typography variant='body2' color={row.isActive ? 'text.primary' : 'text.disabled'}>
                      {row.label}
                    </Typography>
                    <Typography
                      variant='body2'
                      fontWeight={500}
                      color={row.isActive ? 'text.primary' : 'text.disabled'}
                    >
                      {`-${formatCurrency(row.amount)}`}
                    </Typography>
                  </Box>
                ))}

                {/* Net Sales */}
                <Box sx={netSalesRowSx}>
                  <Typography variant='body2' fontWeight={600}>
                    {t('tipShare.contribution.netSales')}
                  </Typography>
                  <Typography variant='body2' fontWeight={600}>
                    {formatCurrency(netSales)}
                  </Typography>
                </Box>

                {/* Tip-share amount header */}
                <Typography variant='body2' fontWeight={600} sx={tipShareAmountTitleSx}>
                  {t('tipShare.contribution.tipShareAmount')}
                </Typography>

                {/* Tip-share calculation formula and result */}
                <Box sx={calculationRowSx}>
                  <Typography variant='body2' color='text.secondary'>
                    {t('tipShare.contribution.tipShareFormula')}
                  </Typography>
                  <Typography variant='body2' fontWeight={600}>
                    {formatCurrency(tipShareAmount)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

TipShareContributionForm.defaultProps = {
  isReadOnly: false,
};
